import axios from '../http-common';

import { useAppSelector } from '../../../redux/hooks';
import { lddGlobalSessionInfo } from '../../navigationTypes';
import { RetainerState } from '../types';

class PortalDataService {
    private SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);

    private urlSuffix = '';

    constructor(state) {
        this.urlSuffix = `?SessionID=${this.SESSION_INFO.sid}&AppID=${state.appId}`;
    }

    getSessionId(){
        return this.SESSION_INFO.sid;
    }

    getSessionAppCode(){
        return this.SESSION_INFO.appCode;
    }

    getProvince(){
        return this.SESSION_INFO.appProvinceShort;
    }

    getFullProvince() {
        return this.SESSION_INFO.appProvince;
    }

    getPortalById(id): Promise<any[]> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/portal/${id}` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    getPortalByAppId(appId): Promise<any[]> {

        const url = `/${this.SESSION_INFO.appProvinceShort}/portal?SessionID=${this.SESSION_INFO.sid}&AppID=${appId}`;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    createPortal(): Promise<number> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/createPortal` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    setSessionDateTime(portalId, date) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/portal/session/setDateTime` + this.urlSuffix;
        const data = {portalId, date};

        return new Promise((resolve, reject) => {
            axios.post(url, data)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    getFileInfo(fileId): Promise<any> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/getInfo` + this.urlSuffix;
        
        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }
                    resolve(response.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    deleteChanges(fileId): Promise<any> {
        const url = `/${this.SESSION_INFO.appProvinceShort}/files/deleteChanges` + this.urlSuffix;

        return new Promise((resolve, reject) => {
            axios.get(url)
                .then((response) => {
                    if (response.data.message === 'error') {
                        throw new Error(response.data.error);
                    }

                    resolve(response.data);
                })
                .catch((error) => {
                    console.log('error', error);
                    reject();
                });
        });
    }

    async joinRemoteSigningPortal(portalId: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/joinRemoteSigningPortal/${portalId}` + this.urlSuffix;
        const res = await axios({ method: 'get', url });

        return res.data;
    }

    async getTwillioToken() {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getTwillioToken` + this.urlSuffix;
        const res = await axios({ method: 'get', url });

        return res.data;
    }

    async getLawyerRemoteSigningLobbyData(appId: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getXMLFileData/${appId}/lawyerRemoteSigningLobby` + this.urlSuffix;

        const res = await axios({ method: 'get', url });

        return res.data;
    }

    async getClientPortalDocuments(portalId: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocuments/${portalId}` + this.urlSuffix;
        const res = await axios({ method: 'get', url });
        return res.data;
    }

    // async getRemoteSigningDocuments(portalId: string) {
    //     const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocuments/${portalId}` + this.urlSuffix;
    //     const res = await axios({ method: 'get', url });
    //     return res.data;
    // }

    async getClientPortalDocumentFromLaywer(portalId, fileName: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocumentFromLaywer/${portalId}/${fileName}` + this.urlSuffix;

        const res = await axios({ method: 'get', url, responseType: 'blob'});
        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }

    async getClientPortalDocumentFromClient(portalId, fileName: string) {
    
        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalDocumentFromClient/${portalId}/${fileName}` + this.urlSuffix;

        const res = await axios({ method: 'get', url, responseType: 'blob'});
        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }

    async deleteClientPortalDocumentFromLawyer(portalId, fileName: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/deleteClientPortalDocumentFromLawyer/${portalId}/${fileName}` + this.urlSuffix;
        const resp = await axios({method: 'delete', url});
        return resp
    }

    async getRemoteSigningDocumentUrl(portalId, fileName: string) {
    
        //const url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;

        let url = ``;
        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;
        } else {
            url = `/clientportal/lawyer/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocumentUrl/${portalId}/${fileName}` + this.urlSuffix;
        }

        return url;
    }

    async setRetainerType(retainerState: RetainerState, appId, portalId) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/${appId}/${portalId}/setRetainerType` + this.urlSuffix;

        let config = {
            method: 'post',
            url: url,
            data: retainerState
        }

        const res = await axios.request(config);
        return res.data;
    }

    async getClientPortalRetainerInformation(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalRetainerInformation/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);
        return res.data;

    }

    async getCurrentRetainer(portalId) {

        const url = `/${this.SESSION_INFO.appProvinceShort}/getCurrentRetainer/${portalId}` + this.urlSuffix;
        const fileName = 'retainer.pdf'

        const res = await axios({ method: 'get', url, responseType: 'blob'});
        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

    }

    async uploadDocument(uploadedFile, portalId, appId, fileName, folder) {

        const url = `/${this.SESSION_INFO.appProvinceShort}/${portalId}/uploadDocument/${folder}/${fileName}` + this.urlSuffix;

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: url,
            headers: { 'Content-Type': 'application/octet-stream' }, // ????
            data : uploadedFile
          };

        const res = await axios.request(config);
        return res.data;
    }

    async getPropertyAttributesState(portalId) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getPropertyAttributesState/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;

    }

    async getDocumentsFromClient(portalId){
        const url = `/${this.SESSION_INFO.appProvinceShort}/getDocumentsFromClient/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getRemoteSigningDocuments(portalId){
        const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocuments/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getRemoteSigningDocument(portalId, fileName){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningDocument/${portalId}/${fileName}` + this.urlSuffix;

        const res = await axios({ method: 'get', url, responseType: 'blob'});
        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }

    async deleteRemoteSigningDocumentFromLawyer(portalId, fileName: string) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/deleteRemoteSigningDocumentFromLawyer/${portalId}/${fileName}` + this.urlSuffix;
        const resp = await axios({method: 'delete', url});
        return resp
    }

    async getDefaultRemoteSigningDocument(portalId, fileName){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getDefaultRemoteSigningDocument/${portalId}/${fileName}` + this.urlSuffix;

        const res = await axios({ method: 'get', url, responseType: 'blob'});
        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }

    async getRemoteSigningRequestFromClient(portalId){
        const url = `/${this.SESSION_INFO.appProvinceShort}/getRemoteSigningRequestFromClient/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getClientInformation(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientInformation/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;

    }

    async getSourceOfFunds(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getSourceOfFunds/${portalId}` + this.urlSuffix;

        let config = {
            method: 'get',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;

    }

    async setClientPortalCustomFields(portalId, customFields, afterOrBeforeInit: string){

        const url = `/${this.SESSION_INFO.appProvinceShort}/setClientPortalCustomFields/${portalId}/${afterOrBeforeInit}` + this.urlSuffix;

        let config = {
            method: 'POST',
            url: url,
            data : customFields
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getClientPortalCustomFields(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getClientPortalCustomFields/${portalId}` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getMortgageInfo(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getMortgageInfo/${portalId}` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async ableToCreatePortal(){

        const url = `/${this.SESSION_INFO.appProvinceShort}/files/ableToCreatePortal/` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getDefaultRemoteSigningFiles(portalId){

        const url = `/${this.SESSION_INFO.appProvinceShort}/getDefaultRemoteSigningFiles/${portalId}` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async addDefaultDocumentToRemoteSigning(portalId, document){

        const url = `/${this.SESSION_INFO.appProvinceShort}/addDefaultDocumentToRemoteSigning/${portalId}` + this.urlSuffix;

        let config = {
            method: 'PUT',
            url: url,
            data : document
        }

        const res = await axios.request(config);

        return res.data;

    }

    async updateClientNames(portalId, clientNames) {

        const url = `/${this.SESSION_INFO.appProvinceShort}/updateClientNames/${portalId}` + this.urlSuffix;

        let config = {
            method: 'POST',
            url: url,
            data : clientNames
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getDocumentRequest(portalId) {

        const url = `/${this.SESSION_INFO.appProvinceShort}/getDocumentRequests/${portalId}` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async resendInvitation(portalId) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/resendInvitation/${portalId}` + this.urlSuffix;

        const config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

    async getSelectedCustomFieldSetsFromXML(portalId) {
        const url = `/${this.SESSION_INFO.appProvinceShort}/getSelectedCustomFieldSet/${portalId}` + this.urlSuffix;

        let config = {
            method: 'GET',
            url: url,
        }

        const res = await axios.request(config);

        return res.data;
    }

}


export default PortalDataService;
